import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Alert } from "react-bootstrap";
import Card from "../../../components/Card";
import Loader from "../../../components/partials/components/loader";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { getApiCall, postApiCall } from "../../../service/httpCall";
import { API_EXTENSION_BULK_CREATE, API_USERS_PROFILE } from "../../../service/url";
import { ROUTES, USER_ROLES } from "../../../utilities/constant";
import { getUser } from "../../../utilities/setting";

const MAX_BULK_EXTENSIONS = 500;
const TEMPLATE_HEADERS = ["extension", "password", "description"];

const generateUsername = (companyCode, extensionCode, extensionInput) => {
  if (!companyCode || !extensionCode || !extensionInput) return "";
  return `${companyCode}${String(extensionCode).padStart(2, "0")}${extensionInput}`;
};

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  return error?.response?.data?.message || error?.message || fallback;
};

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const getCellValue = (row, keys) => {
  const normalizedRow = Object.entries(row || {}).reduce((acc, [key, value]) => {
    acc[normalizeHeader(key)] = value;
    return acc;
  }, {});

  for (const key of keys) {
    const value = normalizedRow[normalizeHeader(key)];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
};

export default function BulkCreateExtension() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyCode, setCompanyCode] = useState("");
  const [rolePrefix, setRolePrefix] = useState("");
  const [defaultPassword, setDefaultPassword] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchCompanyContext = async () => {
      try {
        const data = getUser();
        if (!data?.id) {
          toast.error("User data not found. Please login again.");
          return;
        }

        const prefix =
          data.prefix === null || data.prefix === undefined || data.prefix === 0
            ? 1
            : Number(data.prefix);
        setRolePrefix(String(prefix).padStart(2, "0"));

        const isCallCenterAdmin =
          data?.role === USER_ROLES.CALL_CENTRE_ADMIN ||
          data?.role === USER_ROLES.CALL_CENTRE_TEAM_LEAD;

        let companyCodeValue =
          data?.assignedCompany?.company_code ||
          data?.company?.company_code ||
          data?.assigned_company_details?.company_code ||
          null;

        if (isCallCenterAdmin && !companyCodeValue) {
          const currentProfileUrl = API_USERS_PROFILE.replace(":id", data.id);
          const currentProfile = await getApiCall(currentProfileUrl, {});
          const parentId = currentProfile?.parent_id || null;

          if (parentId) {
            const parentProfileUrl = API_USERS_PROFILE.replace(":id", parentId);
            const parentData = await getApiCall(parentProfileUrl, {});
            companyCodeValue =
              parentData?.assigned_company_details?.company_code ||
              parentData?.assignedCompany?.company_code ||
              parentData?.company?.company_code ||
              null;
          }
        }

        if (!companyCodeValue) {
          const currentProfileUrl = API_USERS_PROFILE.replace(":id", data.id);
          const currentProfile = await getApiCall(currentProfileUrl, {});
          companyCodeValue =
            currentProfile?.assigned_company_details?.company_code ||
            currentProfile?.assignedCompany?.company_code ||
            currentProfile?.company?.company_code ||
            null;
        }

        if (companyCodeValue) {
          setCompanyCode(String(companyCodeValue).padStart(4, "0"));
        } else {
          toast.error("Company code not found. Please contact administrator.");
        }
      } catch (error) {
        toast.error(getErrorMessage(error, "Failed to load extension context."));
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyContext();
  }, []);

  const validPreviewRows = useMemo(
    () => previewRows.filter((row) => !row.error),
    [previewRows]
  );

  const parseExtensionValue = (rawExtension) => {
    const digits = String(rawExtension || "").replace(/\D/g, "");
    if (!digits) {
      return { error: "Extension is required" };
    }

    const prefix = `${companyCode}${rolePrefix}`;
    if (digits.length <= 4) {
      return {
        extensionPart: digits,
        extension: `${prefix}${digits}`,
      };
    }

    if (digits.length >= 7 && digits.length <= 10 && digits.startsWith(prefix)) {
      return {
        extensionPart: digits.slice(prefix.length),
        extension: digits,
      };
    }

    return {
      error: `Extension must be 1-4 digits, or full extension starting with ${prefix}`,
    };
  };

  const buildPreviewRows = (rows, fallbackPassword = defaultPassword) =>
    rows.map((row, index) => {
      const rawExtension = getCellValue(row, [
        "extension",
        "ext",
        "extension_number",
        "extension number",
      ]);
      const parsedExtension = parseExtensionValue(rawExtension);
      const rowPassword =
        getCellValue(row, ["password", "pass"]) || String(fallbackPassword || "").trim();
      const description = getCellValue(row, ["description", "desc"]);
      const errorMessages = [];

      if (parsedExtension.error) {
        errorMessages.push(parsedExtension.error);
      }
      if (!rowPassword || rowPassword.length < 4) {
        errorMessages.push("Password must be at least 4 characters");
      }

      return {
        key: index + 1,
        extensionPart: parsedExtension.extensionPart || rawExtension,
        extension: parsedExtension.extension || "",
        username: parsedExtension.extensionPart
          ? generateUsername(companyCode, rolePrefix, parsedExtension.extensionPart)
          : "",
        password: rowPassword,
        description,
        error: errorMessages.join("; "),
      };
    });

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    setResult(null);
    setPreviewRows([]);
    setUploadedFileName(file?.name || "");

    if (!file) return;
    if (!companyCode || !rolePrefix) {
      toast.error("Company code and extension code are required. Please refresh the page.");
      event.target.value = "";
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheet = workbook.SheetNames[0];
      if (!firstSheet) {
        toast.error("No sheet found in uploaded file.");
        return;
      }

      const sheet = workbook.Sheets[firstSheet];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!rows.length) {
        toast.error("No data rows found in uploaded file.");
        return;
      }
      if (rows.length > MAX_BULK_EXTENSIONS) {
        toast.error(`Cannot upload more than ${MAX_BULK_EXTENSIONS} extensions at once.`);
        return;
      }

      const parsedRows = buildPreviewRows(rows);
      setPreviewRows(parsedRows);
      const invalidCount = parsedRows.filter((row) => row.error).length;
      if (invalidCount > 0) {
        toast.warning(`${invalidCount} row(s) need correction before they can be created.`);
      } else {
        toast.success(`${parsedRows.length} row(s) loaded from file.`);
      }
    } catch (error) {
      toast.error("Failed to read Excel file. Please check the template and try again.");
    } finally {
      event.target.value = "";
    }
  };

  const handleDefaultPasswordChange = (value) => {
    setDefaultPassword(value);
    if (previewRows.length) {
      setPreviewRows((prev) =>
        buildPreviewRows(
          prev.map((row) => ({
            extension: row.extensionPart,
            password: row.password || value,
            description: row.description,
          })),
          value
        )
      );
    }
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        extension: "1001",
        password: "1234",
        description: "Sales extension",
      },
      {
        extension: "1002",
        password: "1234",
        description: "Support extension",
      },
    ], { header: TEMPLATE_HEADERS });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Extensions");
    XLSX.writeFile(workbook, "bulk-extension-template.xlsx");
  };

  const validateForm = () => {
    if (!companyCode || !rolePrefix) {
      toast.error("Company code and extension code are required. Please refresh the page.");
      return false;
    }
    if (previewRows.length === 0) {
      toast.error("Please upload an Excel file first.");
      return false;
    }
    if (previewRows.length > MAX_BULK_EXTENSIONS) {
      toast.error(`Cannot create more than ${MAX_BULK_EXTENSIONS} extensions at once.`);
      return false;
    }
    if (validPreviewRows.length === 0) {
      toast.error("No valid rows to create.");
      return false;
    }
    return true;
  };

  const handleBulkCreate = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setResult(null);
    try {
      const payload = {
        extensions: validPreviewRows.map(({ extension, username, password, description }) => ({
          extension,
          username,
          password,
          description,
        })),
      };
      const data = await postApiCall(API_EXTENSION_BULK_CREATE, payload);
      setResult(data);

      if (data?.created_count > 0) {
        toast.success(`${data.created_count} extension(s) created successfully`);
      }
      if (data?.skipped_count > 0 || data?.failed_count > 0) {
        toast.warning("Some extensions were skipped or failed. Check the result below.");
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to create extensions."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div style={{ marginTop: "20px" }}>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="card-title">Bulk Create Extensions</h4>
              <Button
                label="Back to Extensions"
                className="p-button-secondary p-button-sm"
                onClick={() => navigate(`/${ROUTES.MANAGE_AGENTS.EXTENSION}`)}
              />
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                Upload an Excel file with columns: <strong>extension</strong>, <strong>password</strong>, and optional <strong>description</strong>.
                Extension can be the short 1-4 digit value; the system will generate the full extension.
              </Alert>

              <Row className="mb-3 align-items-start">
                <Col md="3" className="d-flex flex-column">
                  <Form.Label>Company Code</Form.Label>
                  <Form.Control value={companyCode || "Loading..."} readOnly disabled />
                </Col>
                <Col md="3" className="d-flex flex-column">
                  <Form.Label>Extension Code</Form.Label>
                  <Form.Control value={rolePrefix || "Loading..."} readOnly disabled />
                </Col>
                <Col md="4" className="d-flex flex-column">
                  <Form.Label>
                    Upload Excel File <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                  />
                  <small className="text-muted">
                    {uploadedFileName || "Accepted: .xlsx, .xls, .csv"}
                  </small>
                </Col>
                <Col md="2" className="d-flex flex-column">
                  <Form.Label className="invisible">Download Template</Form.Label>
                  <Button
                  style={{marginTop:"-6px", width:"90%"}}
                    label="Download Template"
                    className="btn btn-primary w-80"
                    onClick={downloadTemplate}
                  />
                </Col>
              </Row>

              <Row className="mb-3 align-items-start">
                <Col md="4" className="d-flex flex-column">
                  <Form.Label>Default Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={defaultPassword}
                    onChange={(e) => handleDefaultPasswordChange(e.target.value)}
                    placeholder="Used when row password is blank"
                  />
                  <small className="text-muted">Each row can still provide its own password.</small>
                </Col>
                <Col md="4" className="d-flex flex-column">
                  <Form.Label className="invisible">Create Extensions</Form.Label>
                  <Button
                  style={{width:"50%", marginTop:"-6px"}}
                    label={submitting ? "Creating..." : `Create ${validPreviewRows.length || ""} Extensions`}
                    className="btn btn-primary w-70"
                    disabled={submitting || validPreviewRows.length === 0}
                    onClick={handleBulkCreate}
                  />
                </Col>
              </Row>

              {previewRows.length > 0 && (
                <div className="mb-4">
                  <h6>
                    Preview ({validPreviewRows.length} valid / {previewRows.length} total)
                  </h6>
                  <DataTable value={previewRows.slice(0, 100)} paginator rows={10}>
                    <Column field="key" header="#" />
                    <Column field="extensionPart" header="Entered Extension" />
                    <Column field="extension" header="Full Extension" />
                    <Column field="username" header="Username" />
                    <Column field="description" header="Description" />
                    <Column field="error" header="Validation" />
                  </DataTable>
                  {previewRows.length > 100 && (
                    <small className="text-muted">Showing first 100 preview rows only.</small>
                  )}
                </div>
              )}

              {result && (
                <div>
                  <h6>Bulk Create Result</h6>
                  <p className="mb-2">
                    Requested: {result.requested_count || 0} | Created: {result.created_count || 0} | Skipped: {result.skipped_count || 0} | Failed: {result.failed_count || 0}
                  </p>
                  {Array.isArray(result.skipped) && result.skipped.length > 0 && (
                    <DataTable value={result.skipped} paginator rows={10}>
                      <Column field="row" header="Row" />
                      <Column field="extension" header="Extension" />
                      <Column field="username" header="Username" />
                      <Column field="reason" header="Reason" />
                    </DataTable>
                  )}
                  {Array.isArray(result.failed) && result.failed.length > 0 && (
                    <DataTable value={result.failed} paginator rows={10} className="mt-3">
                      <Column field="row" header="Row" />
                      <Column field="extension" header="Extension" />
                      <Column field="username" header="Username" />
                      <Column field="reason" header="Reason" />
                    </DataTable>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
